/*
 * Copyright 2026 Sony Corporation
 */

import { Box, FormControl, InputLabel, MenuItem, Select, Slider, Tooltip, Typography } from '@mui/material';

import { CREATIVE_LOOK_MODES, CREATIVE_LOOK_SLIDERS, type CreativeLookField } from '../../creative-look/fields';
import type { UseCreativeLookResult } from '../../hooks/use-creative-look';

export interface CreativeLookPanelProps {
  look: UseCreativeLookResult;
}

const Row = ({
  label,
  field,
  look,
  children,
}: {
  label: string;
  field: CreativeLookField;
  look: UseCreativeLookResult;
  children: React.ReactNode;
}) => {
  const error = look.errors[field];
  const pending = look.status[field] === 'pending';
  const greyed = !look.enabled[field];

  return (
    <Box>
      <Box className="flex items-baseline justify-between gap-2">
        <Typography variant="body2" color={greyed ? 'text.disabled' : 'text.secondary'}>
          {label}
        </Typography>
        {pending && (
          <Tooltip title="Sent to the device; waiting for it to confirm.">
            <Typography variant="caption" color="warning.main">
              pending
            </Typography>
          </Tooltip>
        )}
      </Box>
      {children}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};

/**
 * The nine Creative Look controls.
 *
 * A field the device greys out is disabled here rather than left to fail: the
 * body legitimately refuses these edits in some modes and says so in
 * `creativeLookEnabled`, so the UI should never offer a change that will bounce.
 */
export const CreativeLookPanel = ({ look }: CreativeLookPanelProps) => (
  <Box className="flex flex-col gap-3 pt-2">
    <Row label="Look" field="mode" look={look}>
      <FormControl size="small" fullWidth disabled={!look.enabled.mode}>
        <InputLabel id="creative-look-mode">Mode</InputLabel>
        <Select
          labelId="creative-look-mode"
          label="Mode"
          value={look.values.mode}
          onChange={(event) => look.set('mode', event.target.value)}
        >
          {CREATIVE_LOOK_MODES.map((mode) => (
            <MenuItem key={mode} value={mode}>
              {mode}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Row>

    {CREATIVE_LOOK_SLIDERS.map((slider) => (
      <Row key={slider.key} label={slider.label} field={slider.key} look={look}>
        <Slider
          size="small"
          aria-label={slider.label}
          disabled={!look.enabled[slider.key]}
          min={slider.min}
          max={slider.max}
          step={1}
          marks={slider.max - slider.min <= 8}
          valueLabelDisplay="auto"
          value={look.values[slider.key]}
          onChange={(_event, value) => look.set(slider.key, value)}
        />
      </Row>
    ))}
  </Box>
);
